import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Setting, SettingsService } from 'src/app/core/settings/settings.service';
import {
  CUSTOM_PROFILE_ID,
  expandToSettings,
  matchProfile,
  VideoProfile,
  VideoProfileTargetNames,
  VideoProfileValues,
  VIDEO_PROFILES,
  VIDEO_PROFILE_TARGET_SETTINGS,
} from 'src/app/core/settings/video-profile';

/**
 * Lets an operator pick an HD video profile (or enter custom encoder/source
 * strings) and emits the FOUR real settings the device understands. The
 * "HD Video Profile" name itself is never emitted.
 */
@Component({
  selector: 'video-profile-selector',
  templateUrl: './video-profile-selector.component.html',
  styleUrls: ['./video-profile-selector.component.scss'],
})
export class VideoProfileSelectorComponent implements OnInit {
  /** Existing values (when editing), used to pre-select a profile and seed custom fields. */
  @Input() currentValues?: Partial<VideoProfileValues>;
  @Input() updatingSetting = false;

  @Output() settingsAdded = new EventEmitter<Setting[]>();

  readonly profiles = VIDEO_PROFILES;
  readonly customId = CUSTOM_PROFILE_ID;

  selectedId = 'better_quality';
  /** When checked, the four target settings are unpinned (reverted to inherited). */
  unpin = false;
  custom: VideoProfileValues = {
    encoderExterior: 'ENCODER=0,RES=1,FPS=20,BITRATE=3000',
    encoderInterior: 'ENCODER=0,RES=1,FPS=10,BITRATE=2000',
    sourceExterior: 'RES=1,FPS=20',
    sourceInterior: 'RES=1,FPS=20',
  };

  /**
   * Target Names that do NOT resolve to a registered SettingType in the
   * fetched dictionary. The UI shows the Label, but the device + the
   * GetSettingType lookup key on Name (which the model decouples from Label).
   * If a target name isn't registered, expanding the profile would submit a
   * name the API rejects — so we block submit and surface it instead.
   */
  unregisteredTargets: string[] = [];

  /** Resolved registered Names for the four targets (label -> Name). */
  private resolvedNames: Partial<VideoProfileTargetNames> = {};

  constructor(private settingsService: SettingsService) {}

  ngOnInit(): void {
    if (this.currentValues) {
      const cv = this.currentValues;
      // seed the custom fields from current values (fall back to defaults only when undefined)
      this.custom = {
        encoderExterior: cv.encoderExterior ?? this.custom.encoderExterior,
        encoderInterior: cv.encoderInterior ?? this.custom.encoderInterior,
        sourceExterior: cv.sourceExterior ?? this.custom.sourceExterior,
        sourceInterior: cv.sourceInterior ?? this.custom.sourceInterior,
      };

      // Pre-select to MATCH the grid badge: run matchProfile on the raw values
      // (with any 'NA'), so an unset/mismatched row opens on Custom rather than the
      // default profile. Only a completely empty currentValues (fresh new request)
      // keeps the default profile.
      const hasAnyValue = [
        cv.encoderExterior,
        cv.encoderInterior,
        cv.sourceExterior,
        cv.sourceInterior,
      ].some((v) => v !== undefined && v !== null);
      if (hasAnyValue) {
        const match = matchProfile({
          encoderExterior: cv.encoderExterior,
          encoderInterior: cv.encoderInterior,
          sourceExterior: cv.sourceExterior,
          sourceInterior: cv.sourceInterior,
        } as VideoProfileValues);
        this.selectedId = match ? match.id : this.customId;
      }
    }

    this.unregisteredTargets = [];
    (Object.keys(VIDEO_PROFILE_TARGET_SETTINGS) as (keyof VideoProfileTargetNames)[]).forEach(
      (key) => {
        const identifier = VIDEO_PROFILE_TARGET_SETTINGS[key];
        const resolved = this.settingsService.resolveSettingName(identifier);
        if (resolved) {
          this.resolvedNames[key] = resolved;
        } else {
          this.unregisteredTargets.push(identifier);
        }
      },
    );

    // stage the default selection so the modal's Submit Request is ready.
    // deferred to the next tick to avoid ExpressionChangedAfterItHasBeenChecked
    // when the parent's submit-button state reads the freshly staged settings.
    setTimeout(() => this.emitCurrent());
  }

  get canAdd(): boolean {
    return this.unregisteredTargets.length === 0;
  }

  get isCustom(): boolean {
    return this.selectedId === this.customId;
  }

  get selectedProfile(): VideoProfile | undefined {
    return this.profiles.find((p) => p.id === this.selectedId);
  }

  select(id: string): void {
    this.selectedId = id;
    this.emitCurrent();
  }

  /** Re-stage whenever the operator edits the custom encoder/source fields. */
  onCustomChange(): void {
    this.emitCurrent();
  }

  /** Re-stage when toggling Unpin (revert the four settings to inherited). */
  onUnpinChange(): void {
    this.emitCurrent();
  }

  /** Values that will be written: a preset's values, or the Custom fields. */
  get previewValues(): VideoProfileValues {
    if (this.isCustom) {
      return { ...this.custom };
    }
    const p = this.selectedProfile;
    return {
      encoderExterior: p.encoderExterior,
      encoderInterior: p.encoderInterior,
      sourceExterior: p.sourceExterior,
      sourceInterior: p.sourceInterior,
    };
  }

  /**
   * Stages the four expanded settings on the modal. Emits an empty array (which
   * clears the staged settings and disables Submit Request) when a target name
   * is not registered.
   */
  private emitCurrent(): void {
    if (!this.canAdd) {
      this.settingsAdded.emit([]);
      return;
    }

    if (this.unpin) {
      this.settingsAdded.emit(this.unpinSettings());
      return;
    }

    this.settingsAdded.emit(
      expandToSettings(this.previewValues, this.resolvedNames as VideoProfileTargetNames),
    );
  }

  /**
   * Stages the four target settings with a null value to unpin them — same
   * contract as the per-setting "Unpin Value" — so each reverts to the value
   * inherited from the higher tier.
   */
  private unpinSettings(): Setting[] {
    const names = this.resolvedNames as VideoProfileTargetNames;
    return [
      names.encoderExterior,
      names.encoderInterior,
      names.sourceExterior,
      names.sourceInterior,
    ].map((name) => ({ name, value: null, displayValue: 'UNPIN' }));
  }
}

